// Pure helpers for the trend series. Kept out of the hook module so they
// can be tested without pulling in the app provider chain.

export type TrendPoint = {
  /** "YYYY-MM". */
  month: string;
  /** Runs that landed in this month. Labels the month — NOT the denominator
   *  of any single measure; use the per-measure counts for that. */
  runCount: number;
  inventoryUnits: number | null;
  unfulfilledUnits: number | null;
  excessUnits: number | null;
  /** How many runs actually reported each measure. A month of 5 runs where
   *  one reported excess has an excess averaged over 1, and presenting that
   *  as a 5-run average would overstate it. */
  inventoryRunCount: number;
  unfulfilledRunCount: number;
  excessRunCount: number;
  /** The month is still underway: each measure is the LATEST reading (the
   *  current value), not an average, and moves with every run until the
   *  month closes. The chart draws it as provisional. */
  inProgress: boolean;
};

/** Every month between the first and last point, so gaps in the run history
 *  are drawn as gaps rather than silently closed up. A month with no runs is
 *  absent from the series; the chart must not join across it as if the line
 *  were continuous. */
export const monthsSpanning = (points: TrendPoint[]): string[] => {
  if (points.length === 0) {
    return [];
  }
  const [startY, startM] = points[0].month.split("-").map(Number);
  const [endY, endM] = points[points.length - 1].month.split("-").map(Number);
  const months: string[] = [];
  let year = startY;
  let month = startM;
  while (year < endY || (year === endY && month <= endM)) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
};
